/**
 * MODELO DE VIEWMODEL DE PÁGINA DE CADASTRO.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { ContaReceber } from "../../domain/models/conta_receber";
import { ContaReceberForm, ContaReceberSchema, defaultContaReceberForm } from "../schemas/ContasReceber_cadastro/ContasReceber_form";
import { ContasReceberCopiaForm } from "../schemas/ContasReceber_lista/ContasReceber_copia";
import { ContaReceberParcialForm } from "../schemas/ContasReceber_cadastro/ContasReceberParcial_form";
import { useContasReceberListaPagination } from "../hooks/contas_receber_lista/pagination";
import { ContasReceberParcial } from "../../domain/models/recebimento_parcial";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { FileUtils } from "../../../../common/utils/file_utils";
import { ContratoMultaJuros } from "../../domain/models/contrato_multa_juros";
import { useUserAuthStore } from "../../../../stores/global/userAuth";

export const useContasReceberCadastroViewModel = () => {

    const {
        commons: {
            getCentroCustoDropDownUseCase,
            getClientesDropDownUseCase,
            getBancosPagamentoDropDownUseCase,
            getOrdemComprasDropDownUseCase,
            getSubcontasDropDownUseCase,
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
            getFuncionarioByIdUsuarioUseCase
            // getFuncionarioByIdUsuarioUseCase,

        },
        financeiro: {
            getContaReceberByIdUseCase,
            deleteContaReceberByIdUseCase,
            copiaContasReceberUseCase,
            cancelarRecebimentoUseCase,
            createContaReceberUseCase,
            updateContaReceberUseCase,
            createContasReceberParcialUseCase,
            deleteContasReceberParcialUseCase,
            getAllContasReceberParcialUseCase,
            getContaReceberArquivoByIdUseCase,
            getMultaJurosContasReceberNoContratoUseCase,

        } } = useDependencyInjection()

    const location = useLocation();
    const navigate = useNavigate();
    const pagination = useContasReceberListaPagination();
    const user = useUserAuthStore();

    const menuRef = useRef<HTMLButtonElement>(null);
    const [openMenu, setOpenMenu] = useState(false);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const { pushErrorNotification, pushInfoNotification, pushWarnNotification, setPageLoading, setCustomLoading } = usePageLoader();

    const [contaReceberSelectedId, setContaReceberSelectedId] = useState<number | undefined>();
    const [contaReceber, setContaReceber] = useState<ContaReceber>();

    const [contratoMultaJuros, setContratoMultaJuros] = useState<ContratoMultaJuros | null>(null);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [openCopiaModal, setOpenCopiaModal] = useState<boolean>(false);
    const [tituloCopia, setTituloCopia] = useState<string | undefined>();
    const [statusBloqueio, setStatusBloqueio] = useState<boolean>(false);

    // RECEBIMENTO PARCIAL

    const [openDeleteRecebimentoParcialModal, setOpenDeleteRecebimentoParcialModal] = useState<boolean>(false);
    const [contasReceberParcialLista, setContasReceberParcialLista] = useState<ContasReceberParcial[]>();
    const [selectedIdRecebimentoParcialDelete, setSelectedIdRecebimentoParcialDelete] = useState<number | undefined>();
    const [painelRecebimentoParcialOpen, setPainelRecebimentoParcialOpen] = useState(false);
    const [valorTotalParcial, setValorTotalParcial] = useState<number>(0.00)

    //DROPDOWN ESTADOS
    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
    const [ddlCliente, setDdlCliente] = useState<DropDown[]>();
    const [ddlBanco, setDdlBanco] = useState<DropDown[]>();
    const [ddlOrdemCompra, setDdlOrdemCompra] = useState<DropDown[]>();
    const [ddlSubconta, setDdlSubconta] = useState<DropDown[]>();



    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })

    const hookForm = useForm<ContaReceberForm>({
        reValidateMode: 'onSubmit',
        mode: 'onSubmit',
        defaultValues: defaultContaReceberForm,
        resolver: zodResolver(ContaReceberSchema),
    });

    const [idClienteUnidade] = hookForm.watch(["idCliente"]);

    const [valorReceber] = hookForm.watch(["valorReceber"]);
    const [multa] = hookForm.watch(["multa"]);
    const [juros] = hookForm.watch(["juros"]);
    const [desconto] = hookForm.watch(["desconto"]);


    //#region CARREGAMENTO.
    const loadContaReceberSelecionada = useCallback(async (idContaReceber: number) => {
        try {
            setCustomLoading(true, "Carregando dados...");
            const contaReceber = await getContaReceberByIdUseCase.getContaReceberById(idContaReceber)

            hookForm.setValue('idContasReceber', contaReceber.idContasReceber ?? null);
            hookForm.setValue('idCentroCusto', contaReceber.idCentroCusto ?? null);
            hookForm.setValue('idSubconta', contaReceber.idSubconta ?? null);
            hookForm.setValue('idCliente', contaReceber.idCliente ?? null);
            hookForm.setValue('idClienteCentroCusto', contaReceber.idClienteCentroCusto ?? null);
            hookForm.setValue('idOrdemCompra', contaReceber.idOrdemCompra ?? null);
            hookForm.setValue('idBancoPagamento', contaReceber.idBancoPagamento ?? null);
            hookForm.setValue('status', contaReceber.status ?? 0);
            hookForm.setValue('nrContasReceber', contaReceber.nrContasReceber ?? null);
            hookForm.setValue('valorReceber', contaReceber.valorReceber ?? 0);
            hookForm.setValue('multa', contaReceber.multa ?? null);
            hookForm.setValue('juros', contaReceber.juros ?? null);
            hookForm.setValue('desconto', contaReceber.desconto ?? null);
            hookForm.setValue('nome', contaReceber.nome ?? '');
            hookForm.setValue('dataReceber', contaReceber.dataReceber ? new Date(contaReceber.dataReceber) : null);
            hookForm.setValue('dataBaixa', contaReceber.dataBaixa ? new Date(contaReceber.dataBaixa) : null);
            hookForm.setValue('dataCompetencia', contaReceber.dataCompetencia ? new Date(contaReceber.dataCompetencia) : null);
            hookForm.setValue('nrNotaFiscal', contaReceber.nrNotaFiscal ?? '');
            hookForm.setValue('nrDocumentos', contaReceber.nrDocumentos ?? '');
            hookForm.setValue('valorBaixa', contaReceber.valorBaixa ?? null);
            hookForm.setValue('observacao', contaReceber.observacao ?? '');
            hookForm.setValue('formaPagamento', contaReceber.formaPagamento ?? '');
            hookForm.setValue('nomeArquivo1', contaReceber.nomeArquivo1 ?? '');
            hookForm.setValue('nomeArquivo2', contaReceber.nomeArquivo2 ?? '');
            hookForm.setValue('nomeArquivo3', contaReceber.nomeArquivo3 ?? '');

            hookForm.setValue('outros', contaReceber.outros ?? '');


            if (contaReceber.status === 1) {
                setStatusBloqueio(true)
            } else {
                setStatusBloqueio(false)
            }

            setContaReceber(contaReceber)
            setContaReceberSelectedId(idContaReceber);

        } catch (e: unknown) {
            pushErrorNotification("Error", "Falha ao buscar registro da conta a receber!")
        } finally {
            setCustomLoading(false);
        }
    }, [hookForm, getContaReceberByIdUseCase, pushErrorNotification, setCustomLoading])

    const loadContasReceberParcialLista = useCallback(async (idContaReceber: number, page?: number, rowsPerPage?: number) => {
        try {

            const filteredPage = page ?? pagination.page;
            const filteredRowsPerPage = rowsPerPage ?? pagination.rowsPerPage;

            await getAllContasReceberParcialUseCase.getAllContasReceberParcial(
                idContaReceber, filteredPage, filteredRowsPerPage
            )

            setContasReceberParcialLista(getAllContasReceberParcialUseCase.items)
            const somaRecebimentoParcial = getAllContasReceberParcialUseCase.items
                .reduce((total, c) => total + (c.valorBaixa ?? 0), 0);

            setValorTotalParcial(somaRecebimentoParcial)

        } catch (e: unknown) {
            pushErrorNotification("Error", "Falha ao buscar registro da conta a receber Parcial!")
        }
    }, [
        getAllContasReceberParcialUseCase,
        pagination,
        pushErrorNotification
    ])

    const loadMultaJurosContasReceberNoContrato = useCallback(async (idContaReceber: number, isClick: boolean) => {
        try {
            setPageLoading(true);
            const result = await getMultaJurosContasReceberNoContratoUseCase.execute(idContaReceber);
            setContratoMultaJuros(result);
            if (isClick && result) {
                hookForm.setValue("multa", result.valorMulta)
                hookForm.setValue("juros", result.valorJuros)
            }

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar multa e juros do contrato!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getMultaJurosContasReceberNoContratoUseCase,
        hookForm,
        pushErrorNotification,
        setPageLoading
    ]);


    const loadCentroCustoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centro de custo!'));
        } finally {
            setPageLoading(false);
        }
    }, [getCentroCustoDropDownUseCase, pushErrorNotification, setPageLoading]);

    const loadClientesDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getClientesDropDownUseCase.getClientesDropDown();
            setDdlCliente(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar clientes!'));
        } finally {
            setPageLoading(false);
        }
    }, [getClientesDropDownUseCase, pushErrorNotification, setPageLoading]);

    const loadBancosPagamentoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getBancosPagamentoDropDownUseCase.getBancosPagamentoDropDown();
            setDdlBanco(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar bancos!'));
        } finally {
            setPageLoading(false);
        }
    }, [getBancosPagamentoDropDownUseCase, pushErrorNotification, setPageLoading]);

    const loadOrdemCompraDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getOrdemComprasDropDownUseCase.getOrdemComprasDropDown();
            setDdlOrdemCompra(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar ordens de compra!'));
        } finally {
            setPageLoading(false);
        }
    }, [getOrdemComprasDropDownUseCase, setPageLoading, pushErrorNotification]);

    const loadSubcontaDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getSubcontasDropDownUseCase.getSubcontasDropDown();
            setDdlSubconta(result);

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar subcontas!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getSubcontasDropDownUseCase,
        setPageLoading,
        pushErrorNotification]);


    useLayoutEffect(() => {
        setPageLoading(true)
    }, [
        setPageLoading
    ])


    //#endregion



    // #region PAGE START
    usePageStartBootstrap(async () => {
        try {
            await loadCentroCustoDropDown();
            await loadClientesDropDown();
            await loadBancosPagamentoDropDown();
            await loadOrdemCompraDropDown();
            await loadSubcontaDropDown();

            const idContaReceber = contaReceberSelectedId ?? location.state?.idContasReceber as number
            if (idContaReceber) {
                await loadContaReceberSelecionada(idContaReceber)
                await loadContasReceberParcialLista(idContaReceber)
                await loadMultaJurosContasReceberNoContrato(idContaReceber, false)
                setContaReceberSelectedId(idContaReceber)
            } else {
                setStatusBloqueio(true)
            }

            const usuarioInfo = await getFuncionarioByIdUsuarioUseCase.getFuncionarioByIdUsuario(user.idUsuario);
            requestAnimationFrame(() => {
                if (usuarioInfo !== null && usuarioInfo) {
                    hookForm.setValue('idFuncionario', usuarioInfo.idFuncionario);
                }
                setLoading(false);
            });

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao carregar página!'));
        }
    })


    useEffect(() => {
        if (contaReceber && contaReceber.valorBaixa !== undefined) {
            return
        }
        const total = (valorReceber + (multa ?? 0) + (juros ?? 0)) - (desconto ?? 0)
        hookForm.setValue("valorBaixa", total)

    }, [valorReceber, contaReceber, hookForm, multa, juros, desconto])

    //#endregion




    //#region MUDANÇA STATUS/MULTA JUROS

    const handleStatusChange = useCallback(async (newStatus: number) => {
        try {
            const dataBaixa = new Date();
            hookForm.setValue("status", newStatus)
            hookForm.setValue("dataBaixa", dataBaixa)

            if (contaReceber) {
                setContaReceber(param => param ? ({
                    ...param,
                    status: newStatus,
                    dataBaixa: dataBaixa
                }) : undefined);
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", "Falha ao mudar Status!")
        }
    }, [
        hookForm,
        contaReceber,
        pushErrorNotification
    ]);

    const handleAplicarMultaJurosContrato = useCallback(async () => {
        try {
            if (contratoMultaJuros && contaReceberSelectedId) {
                await loadMultaJurosContasReceberNoContrato(contaReceberSelectedId, true);
            }

        } catch (e: unknown) {
            pushErrorNotification("Erro", "Falha ao aplicar multa e juros!")
        }
    }, [
        contratoMultaJuros,
        contaReceberSelectedId,
        loadMultaJurosContasReceberNoContrato,
        pushErrorNotification
    ]);

    //#endregion





    //#region SALVAR/UPDATE/CANCELAR
    const handleSalvarContaReceber = useCallback(async (form: ContaReceberForm) => {
        try {
            setLoading(true);
            setSaving(true);
            setCustomLoading(true, "Salvando conta a receber...");
            if (form.arquivo1 !== null) FileUtils.validateFileSize(form.arquivo1);
            if (form.arquivo2 !== null) FileUtils.validateFileSize(form.arquivo2);
            if (form.arquivo3 !== null) FileUtils.validateFileSize(form.arquivo3);

            if (form.status === 1 && (!form.dataBaixa || !form.valorBaixa)) {
                pushWarnNotification("Atenção", "Para marcar como recebido, é necessário informar a data e o valor do recebimento!");
                setSaving(false);
                setLoading(false);
                return;
            }

            if (form.valorReceber && form.valorReceber <= 0) {
                pushWarnNotification("Atenção", "O valor a receber deve ser positivo!");
                setSaving(false);
                setLoading(false);
                return;
            }

            if (contaReceberSelectedId) {
                const idContaReceber = await updateContaReceberUseCase.updateContaReceber({
                    idContasReceber: contaReceberSelectedId ?? undefined,
                    idCentroCusto: form.idCentroCusto ?? undefined,
                    idSubconta: form.idSubconta ?? undefined,
                    idCliente: form.idCliente ?? undefined,
                    idClienteCentroCusto: form.idClienteCentroCusto ?? undefined,
                    idOrdemCompra: form.idOrdemCompra ?? undefined,
                    idFuncionario: form.idFuncionario ?? undefined,
                    nrContasReceber: form.nrContasReceber ?? undefined,
                    valorReceber: form.valorReceber ?? undefined,
                    multa: form.multa ?? undefined,
                    juros: form.juros ?? undefined,
                    desconto: form.desconto ?? undefined,
                    nome: form.nome ?? undefined,
                    dataReceber: form.dataReceber ? new Date(form.dataReceber) : undefined,
                    dataCompetencia: form.dataCompetencia ? new Date(form.dataCompetencia) : undefined,
                    nrNotaFiscal: form.nrNotaFiscal ?? undefined,
                    nrDocumentos: form.nrDocumentos ?? undefined,
                    observacao: form.observacao ?? undefined,
                    nomeArquivo1: form.nomeArquivo1 ?? undefined,
                    nomeArquivo2: form.nomeArquivo2 ?? undefined,
                    nomeArquivo3: form.nomeArquivo3 ?? undefined,
                    arquivo1: form.arquivo1 !== null ? form.arquivo1 : undefined,
                    arquivo2: form.arquivo2 !== null ? form.arquivo2 : undefined,
                    arquivo3: form.arquivo3 !== null ? form.arquivo3 : undefined,

                    outros: form.outros ?? undefined,
                    idBancoPagamento: form.idBancoPagamento ?? undefined,
                    status: form.status ?? undefined,
                    dataBaixa: form.dataBaixa ? new Date(form.dataBaixa) : undefined,
                    valorBaixa: form.valorBaixa ?? undefined,
                    formaPagamento: form.formaPagamento ?? undefined,
                    limparArquivo1: form.limparArquivo1 ?? undefined,
                    limparArquivo2: form.limparArquivo2 ?? undefined,
                    limparArquivo3: form.limparArquivo3 ?? undefined,

                });

                setContaReceberSelectedId(idContaReceber);
                await loadContaReceberSelecionada(idContaReceber);
                pushInfoNotification("SUCESSO", "Alteração feita com sucesso!!", { isAutoFinish: true })
            } else {
                const idContaReceber = await createContaReceberUseCase.createContaReceber({
                    idCentroCusto: form.idCentroCusto ?? undefined,
                    idSubconta: form.idSubconta ?? undefined,
                    idCliente: form.idCliente ?? undefined,
                    idClienteCentroCusto: form.idClienteCentroCusto ?? undefined,
                    idOrdemCompra: form.idOrdemCompra ?? undefined,
                    idFuncionario: form.idFuncionario ?? undefined,
                    nrContasReceber: form.nrContasReceber ?? undefined,
                    valorReceber: form.valorReceber ?? undefined,
                    multa: form.multa ?? undefined,
                    juros: form.juros ?? undefined,
                    desconto: form.desconto ?? undefined,
                    nome: form.nome ?? undefined,
                    dataReceber: form.dataReceber ? new Date(form.dataReceber) : undefined,
                    dataCompetencia: form.dataCompetencia ? new Date(form.dataCompetencia) : undefined,
                    nrNotaFiscal: form.nrNotaFiscal ?? undefined,
                    nrDocumentos: form.nrDocumentos ?? undefined,
                    observacao: form.observacao ?? undefined,
                    nomeArquivo1: form.nomeArquivo1 ?? undefined,
                    nomeArquivo2: form.nomeArquivo2 ?? undefined,
                    nomeArquivo3: form.nomeArquivo3 ?? undefined,
                    arquivo1: form.arquivo1 !== null ? form.arquivo1 : undefined,
                    arquivo2: form.arquivo2 !== null ? form.arquivo2 : undefined,
                    arquivo3: form.arquivo3 !== null ? form.arquivo3 : undefined,

                });

                setContaReceberSelectedId(idContaReceber);
                await loadContaReceberSelecionada(idContaReceber);
                pushInfoNotification("SUCESSO", "Conta a Receber criada com sucesso!!", { isAutoFinish: true })
            }

        } catch (e: unknown) {
            pushErrorNotification("Erro", ["Falha ao salvar conta a receber"], { isAutoFinish: true })
        } finally {
            setSaving(false);
            setLoading(false);
            setCustomLoading(false);

        }
    }, [
        createContaReceberUseCase,
        updateContaReceberUseCase,
        contaReceberSelectedId,
        setCustomLoading,
        loadContaReceberSelecionada,
        pushInfoNotification,
        pushErrorNotification,
        pushWarnNotification,
    ]);


    const handleCancelarRecebimento = useCallback(async () => {
        try {
            setSaving(true);

            if (contaReceber && contaReceber.idContasReceber) {
                await cancelarRecebimentoUseCase.cancelarRecebimento(contaReceber.idContasReceber);
                await loadContaReceberSelecionada(contaReceber.idContasReceber);
                // pushInfoNotification("Sucesso", "Recebimento cancelado com sucesso!", { isAutoFinish: true });

            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao cancelar recebimento!"));
        } finally {
            setSaving(false);
        }
    }, [
        contaReceber,
        cancelarRecebimentoUseCase,
        loadContaReceberSelecionada,
        pushErrorNotification,
    ]);



    //#endregion





    //#region CABEÇALHO
    const handlePageBack = useCallback(() => {
        navigate('/ContasReceber_lista')
    }, [navigate]);

    const handleNewContaReceber = useCallback(() => {

        hookForm.reset();
        setContaReceber(undefined);
        setContaReceberSelectedId(undefined);
        setStatusBloqueio(true);
        setContasReceberParcialLista(undefined);

    }, [hookForm]);

    const handleCloseMenu = useCallback((event: any) => {
        if (
            menuRef.current &&
            menuRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }
        setOpenMenu(false);
    }, []);

    const handleToggleMenu = useCallback(() => {
        setOpenMenu((prev) => !prev);
    }, []);

    const handleListKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpenMenu(false);
        } else if (event.key === 'Escape') {
            setOpenMenu(false);
        }
    }, []);

    //#endregion





    //#region DELEÇAO
    const handleContaReceberDeleteOpenState = useCallback((idContasReceber: number | null, openState: boolean) => {
        if (openState && idContasReceber) {
            setOpenDeleteModal(openState)
        } else {
            setOpenDeleteModal(openState)
        }

    }, []);

    const handleConfirmarDeletarContaReceber = useCallback(async () => {
        try {
            setSaving(true);
            setLoading(true);
            if (contaReceberSelectedId) {
                await deleteContaReceberByIdUseCase.deleteContaReceberByIds([contaReceberSelectedId], "cadastro");
                pushInfoNotification("Sucesso", "Conta a receber deletada com sucesso!");

            };
            await handlePageBack();

        } catch (e: unknown) {
            throw new Error("Falha ao deletar Centro Custo da lista!");
        } finally {
            setSaving(false);
            setLoading(false);
        }
    }, [
        contaReceberSelectedId,
        deleteContaReceberByIdUseCase,
        pushInfoNotification,
        handlePageBack
    ]);

    //#endregion





    //#region COPIA
    const handleContaReceberCopiaOpenState = useCallback((openState: boolean) => {
        if (openState && contaReceber) {
            setTituloCopia(contaReceber.nome ?? '');
            setOpenCopiaModal(true);

        } else {
            setOpenCopiaModal(false);
            setTituloCopia('');
        }
    }, [contaReceber]);

    const handleSubmitCopiaContaReceber = useCallback(async (form: ContasReceberCopiaForm) => {
        try {
            if (contaReceberSelectedId) {
                setSaving(true);
                await copiaContasReceberUseCase.copiaContasReceber(form)
                pushInfoNotification("Sucesso", "Conta a Receber copiada com sucesso!", { isAutoFinish: true });

            } else {
                pushWarnNotification("Atenção", "Nenhuma conta a receber selecionada para cópia!");
                return;
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao copiar Conta a Receber!"));
        } finally {
            setOpenCopiaModal(false);
            setTituloCopia('');
            setSaving(false);
        }
    }, [
        contaReceberSelectedId,
        copiaContasReceberUseCase,
        pushInfoNotification,
        pushErrorNotification,
        pushWarnNotification,
    ]);

    //#endregion



    //#region ARQUIVOS

    const handleDownloadArquivo = useCallback(async (nroArquivo: number, imageName: string) => {
        if (!saving && contaReceberSelectedId) {
            try {
                setCustomLoading(true, "Baixando arquivo...");
                await getContaReceberArquivoByIdUseCase.execute(contaReceberSelectedId, nroArquivo, imageName);
            } catch (e: unknown) {
                pushErrorNotification("Erro", processError(e, 'Falha ao fazer download do arquivo!'));
            } finally {
                setCustomLoading(false);
            }
        };
    }, [
        saving,
        contaReceberSelectedId,
        getContaReceberArquivoByIdUseCase,
        setCustomLoading,
        pushErrorNotification,
    ]);


    const handleArquivoDeleteOpenState = useCallback((nroArquivo?: number) => {
        switch (nroArquivo) {
            case 1:
                setContaReceber(prev => prev ? ({ ...prev, nomeArquivo1: undefined }) : undefined)
                break;
            case 2:
                setContaReceber(prev => prev ? ({ ...prev, nomeArquivo2: undefined }) : undefined)
                break;
            case 3:
                setContaReceber(prev => prev ? ({ ...prev, nomeArquivo3: undefined }) : undefined)
                break;
            default:
                break;
        }

    }, []);

    //#endregion





    //#region RECEBIMENTO PARCIAL

    const handleCreateContasReceberParcial = useCallback(async (form: ContaReceberParcialForm) => {
        try {

            if (contaReceberSelectedId) {
                const response = await createContasReceberParcialUseCase.createContasReceberParcial({
                    ...form,
                    idContasReceber: contaReceberSelectedId
                })
                await loadContasReceberParcialLista(contaReceberSelectedId)
                if (response.ultimaConta) {
                    await loadContaReceberSelecionada(contaReceberSelectedId)
                    await loadContasReceberParcialLista(contaReceberSelectedId)
                }

                pushInfoNotification("Sucesso", "Recebimento salvo com sucesso")
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar recebimento, Valor cadastrado excede o valor a receber!!"), { isAutoFinish: true })
        } finally {
            setPainelRecebimentoParcialOpen(false)
        }
    }, [
        contaReceberSelectedId,
        createContasReceberParcialUseCase,
        loadContaReceberSelecionada,
        loadContasReceberParcialLista,
        pushInfoNotification,
        pushErrorNotification
    ]);

    const handleContasReceberParcialCadastroOpenState = useCallback((openState: boolean) => {
        setPainelRecebimentoParcialOpen(openState)
    }, []);

    const handleContasReceberParcialDeleteOpenState = useCallback((idRecebimentoParcial: number | null, openState: boolean) => {
        if (openState && idRecebimentoParcial) {
            setOpenDeleteRecebimentoParcialModal(openState)
            setSelectedIdRecebimentoParcialDelete(idRecebimentoParcial)
        } else {
            setOpenDeleteRecebimentoParcialModal(openState)
            setSelectedIdRecebimentoParcialDelete(undefined)
        }

    }, []);

    const handleConfirmarDeletarRecebimentoParcial = useCallback(async () => {
        try {
            setSaving(true);
            setLoading(true);
            if (contaReceber) {
                if (selectedIdRecebimentoParcialDelete) {
                    await deleteContasReceberParcialUseCase.deleteContasReceberParcial(selectedIdRecebimentoParcialDelete)

                    pushInfoNotification("Sucesso", "Recebimento parcial deletado com sucesso!");
                    await loadContasReceberParcialLista(contaReceber.idContasReceber!)
                    setOpenDeleteRecebimentoParcialModal(false)
                }
            };
        } catch (e: unknown) {
            pushErrorNotification("Erro", "Falha ao deletar recebimento parcial!");
        } finally {
            setSaving(false);
            setLoading(false);
        }
    }, [
        contaReceber,
        selectedIdRecebimentoParcialDelete,
        deleteContasReceberParcialUseCase,
        pushInfoNotification,
        pushErrorNotification,
        loadContasReceberParcialLista,
    ]);

    //#endregion

    //#region TABELA RECEBIMENTO PARCIAL

    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

    const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
        setRowSelectionModel(rows);
    }, []);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            if (contaReceberSelectedId) {
                await loadContasReceberParcialLista(contaReceberSelectedId, page);
            }
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a receber Parcial!'));
        } finally {
            setLoading(false);
        }
    }, [pagination,
        contaReceberSelectedId,
        loadContasReceberParcialLista,
        pushErrorNotification]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            if (contaReceberSelectedId) {
                await loadContasReceberParcialLista(contaReceberSelectedId, 1, rowsPerPage);
            }
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [pagination,
        contaReceberSelectedId,
        loadContasReceberParcialLista,
        pushErrorNotification
    ]);

    //#endregion

    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(contaReceber?.idContasReceber ?? 0, LogTipoTabela.CONTAS_RECEBER, true);
            }
        }
    }, [
        contaReceber,
        changeHistory,
    ]);

    return {
        saving,
        changeHistory,
        loading,
        pagination,
        hookForm,
        openMenu,
        menuRef,
        ddlCentroCusto,
        ddlBanco,
        ddlCliente,
        ddlOrdemCompra,
        ddlSubconta,
        contaReceber,
        contaReceberSelectedId,
        openDeleteModal,
        openCopiaModal,
        tituloCopia,
        statusBloqueio,
        idClienteUnidade,
        openDeleteRecebimentoParcialModal,
        selectedIdRecebimentoParcialDelete,
        contasReceberParcialLista,
        rowSelectionModel,
        valorTotalParcial,
        painelRecebimentoParcialOpen,
        contratoMultaJuros,


        setPainelRecebimentoParcialOpen,
        handleAplicarMultaJurosContrato,
        handlePageChange,
        handleRowsPerPageChange,
        handleRowSelectionModelChange,
        handleContasReceberParcialCadastroOpenState,
        handleArquivoDeleteOpenState,
        handleNewContaReceber,
        handleCancelarRecebimento,
        handleStatusChange,
        handleContaReceberCopiaOpenState,
        handleSubmitCopiaContaReceber,
        handleContaReceberDeleteOpenState,
        handleConfirmarDeletarContaReceber,
        handleDownloadArquivo,
        handleCloseMenu,
        handleListKeyDown,
        handleToggleMenu,
        handleOpenChangeHistory,
        handlePageBack,
        handleSalvarContaReceber: hookForm.handleSubmit(handleSalvarContaReceber),
        handleConfirmarDeletarRecebimentoParcial,
        handleCreateContasReceberParcial,
        handleContasReceberParcialDeleteOpenState,

    }

}


